/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Row,
  Col,
  Spin,
  Form,
  Input,
  DatePicker,
  Menu,
  message,
} from 'antd';
import Table from 'components/Table';
import Select from 'components/Select';
import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import SearchControls from 'components/SearchControls';
import { useBitacoraDescuentos, ACCIONES_DESCUENTOS } from 'hooks/useBitacoraDescuentos';
import { getReporteBitacora, getReporteBitacoraID } from 'api/reporteador';
import { onError } from 'utils/handlers';
import Previewer, { downloadBase64 } from 'components/Previewer';
import ModalDetalleBitacoraGrupo from './ModalDetalleBitacoraGrupo';

const TableBitacoraDescuentos = () => {
  const [formSearch] = Form.useForm();
  const [openModal, setOpenModal] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [visiblePreviewer, setVisiblePreviewer] = useState(false);
  const [reportBase64, setReportBase64] = useState('');

  const {
    loading,
    usuarios,
    data,
    selected,
    selectedRowKeys,
    currentPage,
    totalChunks,
    getColumns,
    wipe,
    handleOnRowClick,
    rowSelection,
    handleSearch,
    handlePageChange,
    setSelected,
    searchParams,
    prediosData,
  } = useBitacoraDescuentos();

  const nombreReporte = ACCIONES_DESCUENTOS
    .find((a) => a.id === searchParams.accion_inf)?.descripcion || 'Bitácora de movimientos';

  const handleExportarReporte = async (type) => {
    if (!searchParams.fecha_inicio || !searchParams.fecha_fin) {
      message.warning('Debe realizar una búsqueda con un rango de fechas (inicio y fin) para exportar el reporte');
      return;
    }
    if (!getReporteBitacoraID(searchParams.accion_inf)) {
      message.warning('El reporte para la acción seleccionada aún no está disponible');
      return;
    }
    try {
      setReportLoading(true);
      // Los errores de la petición ya los notifica la capa de api
      const [error, base64] = await getReporteBitacora({
        fecha_inicio: searchParams.fecha_inicio,
        fecha_fin: searchParams.fecha_fin,
        accion: searchParams.accion_inf,
      }, type);
      if (error) return;
      // El XLSX no se puede previsualizar en el iframe, se descarga directo
      if (type === 'excel') {
        downloadBase64(base64, `${nombreReporte}.xlsx`);
        return;
      }
      setReportBase64(base64);
      setVisiblePreviewer(true);
    } catch (error) {
      onError(error);
    } finally {
      setReportLoading(false);
    }
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key="reporte_pdf" onClick={() => handleExportarReporte('pdf')}>
        <FilePdfOutlined />
        PDF
      </Menu.Item>
      <Menu.Item key="reporte_excel" onClick={() => handleExportarReporte('excel')}>
        <FileExcelOutlined />
        Excel
      </Menu.Item>
    </Menu>
  );

  const handleFormSearch = async () => {
    try {
      await formSearch.validateFields();
      const formValues = formSearch.getFieldsValue();
      const { usuario_creo, fecha_inicio, fecha_fin } = formValues;
      const hasUsuario = !!usuario_creo;
      const hasRangoFechas = !!(fecha_inicio && fecha_fin);
      if (!hasUsuario && !hasRangoFechas) {
        message.warning('Debe seleccionar un usuario o un rango de fechas completo (inicio y fin)');
        return;
      }
      await handleSearch(formValues);
    } catch (error) {
      // manejo en el hook
    }
  };

  const handleWipe = () => {
    const defaultValues = wipe();
    formSearch.resetFields();
    formSearch.setFieldsValue(defaultValues);
  };

  return (
    <Spin tip="Cargando..." spinning={loading || reportLoading}>
      <Form
        form={formSearch}
        layout="vertical"
        scrollToFirstError
        onFinish={handleFormSearch}
        className="without-feedback"
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Usuario" name="usuario_creo">
              <Select
                dataSource={usuarios}
                labelProp="nombre_completo"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Clave" name="clave">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="No. Cuenta" name="cuenta_unica">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              label="Acción"
              name="accion_inf"
              rules={[{ required: true, message: 'La acción es obligatoria' }]}
            >
              <Select
                dataSource={ACCIONES_DESCUENTOS}
                labelProp="descripcion"
                valueProp="id"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Fecha inicio" name="fecha_inicio">
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Fecha fin" name="fecha_fin">
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <SearchControls
            onSearch={handleFormSearch}
            onClear={handleWipe}
            searchLoading={loading}
          />
        </Row>
      </Form>

      <Table
        cols={getColumns()}
        data={data.results}
        count={totalChunks * 10}
        rowSelection={rowSelection}
        rowKey="id"
        handleOnRowClick={handleOnRowClick}
        currentPage={currentPage}
        customActions={[
          {
            text: 'Exportar',
            icon: <DownloadOutlined />,
            disabledBySelection: false,
            disabled: reportLoading,
            menu: exportMenu,
          },
          {
            text: 'Historial',
            icon: <HistoryOutlined />,
            tooltip: 'Ver historial',
            disabledBySelection: true,
            onClick: () => {
              const rec = data.results.find((r) => r.id === selectedRowKeys[0]);
              if (!rec) return;
              setSelected(rec);
              setOpenModal(true);
            },
          },
        ]}
        pageSize={10}
        pagination={false}
        loading={loading}
        paginationCallback={handlePageChange}
      />

      <ModalDetalleBitacoraGrupo
        visible={openModal}
        onClose={() => setOpenModal(false)}
        selectedRow={selected}
        predioInfo={prediosData[selected?.clave]}
      />

      <Previewer
        visible={visiblePreviewer}
        setVisible={setVisiblePreviewer}
        base64={reportBase64}
        allowDownload
        fileName={`${nombreReporte}.pdf`}
      />
    </Spin>
  );
};

export default TableBitacoraDescuentos;
